import { api, delay, USE_MOCK } from "@/api/client";

// Third-party connector apps powered by Composio. The set of apps is configured
// by an admin on the backend; each user connects their own account, and the
// connected app's tools become available to Ask Ubik in chat.

export interface ComposioApp {
  toolkit: string;   // e.g. "SLACK"
  name: string;      // e.g. "Slack"
  logo: string;      // logo URL
  /** Composio category name (e.g. "Productivity"), when the backend supplies it.
   * Used to group the "Add apps" catalog into buckets; falls back to a
   * client-side slug→category map (app-categories.ts) when absent. */
  category?: string;
}

export interface ComposioCatalog {
  enabled: boolean;
  apps: ComposioApp[];
}

export interface ComposioStatus {
  enabled: boolean;
  connected: string[];   // uppercased toolkit slugs the user has connected
  /** Toolkits whose connection EXPIRED and has no active replacement (older backends omit it). */
  needs_reconnect?: string[];
}

export const composioApi = {
  /** Apps available to connect (admin-configured). */
  getCatalog: async (): Promise<ComposioCatalog> => {
    if (USE_MOCK) {
      const { mockComposioCatalog } = await import("@mock/composio");
      await delay(80);
      return mockComposioCatalog;
    }
    return api.get<ComposioCatalog>("/v1/integrations/composio/catalog");
  },

  /** Toolkits the current user has an active connection to. */
  getStatus: async (): Promise<ComposioStatus> => {
    if (USE_MOCK) {
      const { getMockComposioStatus } = await import("@mock/composio");
      await delay(50);
      return getMockComposioStatus();
    }
    return api.get<ComposioStatus>("/v1/integrations/composio/status");
  },

  /** Search the full Composio app catalog (1000+ apps). Empty q → most-used. */
  searchToolkits: async (q: string, limit = 30): Promise<{ enabled: boolean; apps: ComposioApp[] }> => {
    if (USE_MOCK) {
      const { searchMockComposioToolkits } = await import("@mock/composio");
      await delay(80);
      return { enabled: true, apps: searchMockComposioToolkits(q, limit) };
    }
    return api.get<{ enabled: boolean; apps: ComposioApp[] }>(
      `/v1/integrations/composio/toolkits?q=${encodeURIComponent(q)}&limit=${limit}`,
    );
  },

  /** Start an OAuth connection; returns the hosted auth URL to redirect to.
   * Pass `{ popup: true }` when connecting from a popup tab (e.g. the chat
   * composer) so the backend returns to a self-closing success page instead
   * of the full Integrations page. */
  connect: async (toolkit: string, opts?: { popup?: boolean }): Promise<{ auth_url: string }> => {
    if (USE_MOCK) {
      const { connectMockComposio } = await import("@mock/composio");
      await delay(120);
      return connectMockComposio(toolkit);
    }
    return api.post<{ auth_url: string }>(
      `/v1/integrations/composio/${encodeURIComponent(toolkit)}/connect`,
      { redirect_base: window.location.origin, popup: !!opts?.popup },
    );
  },

  /** Disconnect the user's account for a toolkit. */
  disconnect: async (toolkit: string): Promise<{ ok: boolean }> => {
    if (USE_MOCK) {
      const { disconnectMockComposio } = await import("@mock/composio");
      await delay(80);
      return disconnectMockComposio(toolkit);
    }
    return api.post<{ ok: boolean }>(
      `/v1/integrations/composio/${encodeURIComponent(toolkit)}/disconnect`,
      {},
    );
  },
};
